/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  TChannel,
  TGridGuideRow,
  TGuideCell,
  TPreview,
  DateTime
} from "../../model/types";

/**
 * Discriminator indicating which time-window bucket a loadGuideRows
 * fetch should populate.
 */
export enum GuideTimeWindow {
  /** Rows for the previous time window. */
  PAST = "past",
  /** Rows for the on-screen time window. */
  CURRENT = "current",
  /** Rows for the next time window. */
  FUTURE = "future"
}

/**
 * Navigation engine mode for the guide grid. Drives "latest wins" determinism
 * and lets the view distinguish a settled grid from one that is fast-scrolling
 * or waiting on the visible window's data.
 */
export enum GuideNavMode {
  /** Focus is settled and the visible window's data is present. */
  IDLE = "idle",
  /** A discrete D-pad step is in progress (short press). */
  NAVIGATING = "navigating",
  /** A key is held: the window moves without fetching intermediate windows. */
  TURBO = "turbo",
  /** The final window has been chosen and its visible data is loading. */
  SETTLING = "settling",
  /** The visible fetch failed and a bounded retry is under way. */
  RECOVERING = "recovering",
  /** Retries are exhausted; focusable fallback cells are shown. */
  ERROR = "error"
}

/**
 * Identifiers for the guide filter sidebar categories.
 *
 * Single source of truth for category ids. When a backing service provides
 * the categories, only the values here (and the GUIDE_FILTER_CATEGORIES list)
 * need to change.
 */
export enum GuideFilterCategoryId {
  FAVORITE = "favorite",
  DRAMA = "drama",
  MOVIES = "movies",
  SPORTS = "sports",
  KIDS = "kids"
}

/**
 * A single dynamic guide filter item, sourced from the `/v2/guideMenuItems` API.
 */
export interface TGuideFilterItem {
  /** Opaque server menu item ID (e.g. "tivo:fgmi.1") or "favorites" for the
   * client-side Favorites fallback. */
  id: string;
  /** Server-localised display label. */
  label: string;
  /**
   * Pre-resolved default (unfocused) icon URL, or `null` when the image-host
   * endpoint was not yet available at fetch time or the ID was blank.
   */
  iconUrl: string | null;
  /**
   * Pre-resolved focused-state icon URL, or `null` under the same conditions
   * as `iconUrl`.
   */
  focusedIconUrl: string | null;
  /**
   * `true` for the one Favorites filter in the list.
   * Used to determine toggle-in-place behaviour vs. navigation.
   */
  isFavorite: boolean;
}

/**
 * Nested state for guide rows (EPG program grid data).
 *
 * Holds per-station program row data fetched from the GridGuideService,
 * split into past / current / future time-window buckets.
 */
export interface GuideRowsState {
  /** Guide rows for the current (on-screen) time window. */
  currentRows: TGridGuideRow[];

  /** Guide rows for the next (future) time window. */
  futureRows: TGridGuideRow[];

  /** Guide rows for the previous (past) time window. */
  pastRows: TGridGuideRow[];
}

/**
 * Payload type for the loadGuideRows async thunk argument.
 */
export interface LoadGuideRowsPayload {
  /** Channels for which to fetch guide rows. */
  channels: TChannel[];

  /** Start time of the requested guide window. */
  windowStartTime: DateTime;

  /** Which time-window bucket the fetched rows should populate. */
  timeWindow: GuideTimeWindow;
}

/**
 * Describes a guide window pre-fetch target.
 * Used by navigation thunks to fetch either the past or future bucket.
 */
export interface GuidePrefetchTarget {
  /** Window bucket to populate (PAST or FUTURE). */
  timeWindow: GuideTimeWindow;

  /** Start time of the window to fetch. */
  windowStartTime: DateTime;
}

/** UI-only computed properties for a rendered guide program cell. */
export interface GuideCellUiProps {
  /** Computed render id — unique within the visible guide grid. */
  id: string;
  /** Cell width in minutes, clipped to the visible window. */
  width: number;
  isFocused: boolean;
  isDimmed: boolean;
  /** True when the cell is a loading placeholder filling an ungapped span. */
  isPlaceholder?: boolean;
  /**
   * True when the cell is the focusable "Select to tune to channel" fallback
   * rendered in place of program data because the guide-rows service failed
   * to load (see GuideState.isGuideRowsFailed). Unlike isPlaceholder, this
   * cell is focusable and tunable.
   */
  isTuneFallback?: boolean;
  /**
   * True when a loading placeholder has exceeded GUIDE_LOADING_CELL_TIMEOUT_MS.
   * Rendered as a navigable, non-selectable "TBA" cell; reverts to a real cell
   * automatically once data arrives.
   */
  isTba?: boolean;
}

/** Render model for a guide program cell. */
export interface GuideCellView {
  /** Raw domain cell data. Stubbed for placeholder cells. */
  props: TGuideCell;
  /** Computed UI presentation properties for this cell. */
  uiProps: GuideCellUiProps;
}

/** Render model for a guide row. */
export interface GuideVisibleRowView {
  channel: TChannel;
  rowIndex: number;
  isChannelFocused: boolean;
  /** True when the channel has a SOCU (start-over / catch-up) policy with a non-zero availability window. */
  hasSocu: boolean;
  primaryPrograms: GuideCellView[];
  secondaryPrograms: GuideCellView[];
}

/** Visible channel row mapping (channel + absolute row index). */
export interface GuideVisibleChannelRow {
  channel: TChannel;
  rowIndex: number;
}

/**
 * Sub-state for the jump-to-channel digit-entry overlay in the guide grid.
 */
export interface GuideJumpToChannelState {
  /** Accumulated digit string (e.g. "42"). Empty when inactive. */
  digits: string;
  /** Whether the overlay pill is currently visible. */
  isVisible: boolean;
  /** Whether the overlay is displaying a "Not found" error. */
  isError: boolean;
}

/**
 * Redux state interface for the Guide (EPG) feature slice.
 *
 * Holds loading/error state for channel fetching, program row data,
 * and focus routing metadata for D-pad navigation.
 */
export interface GuideState {
  /** Channels currently displayed in the guide grid. */
  channels: TChannel[];

  /** Total channel count from the last fetch (for pagination). */
  channelCount: number;

  /** Error message from the last failed data fetch (empty string when no error). */
  error: string;

  /** Whether guide data is currently being fetched. */
  isLoading: boolean;

  /** Current guide window start time as a DateTime instance, set on mount. */
  currentWindowStartTime: DateTime | null;

  /** Whether the guide is displaying past program data. */
  isPastGuide: boolean;

  /** Nested state for EPG program row data. */
  guideRows: GuideRowsState;

  /** Focused row index in the guide grid (0-indexed). */
  verticalPosition: number;

  /**
   * Focused horizontal position in the row.
   * 0 = channel cell, 1..N = program cell index + 1.
   */
  horizontalPosition: number;

  /**
   * Index of the first channel rendered at verticalPosition 0.
   * Increments/decrements circularly on each D-pad press at the lock-zone boundary.
   * Persisted in Redux; reset only on resetGuideState.
   */
  channelScrollOffset: number;

  /** Anchor time of the pre-fetched future window (managed by listener middleware). */
  futureWindowStartTime: DateTime | null;

  /** Anchor time of the pre-fetched past window (managed by listener middleware). */
  pastWindowStartTime: DateTime | null;

  /**
   * Millisecond timestamp of the live "now" the guide's navigation limits are
   * measured against. Re-anchored to the current time on every guide mount
   * (setCurrentWindowStartTime) and on every ~30-minute auto-refresh tick
   * thereafter — whether or not that tick actually snaps the visible window
   * (see refreshGuideSessionAnchor / applyGuideSessionAnchorRefresh) — so a
   * guide left open, backgrounded, or parked in a past/future window for a
   * long duration never navigates against a stale anchor. Used by reducers to
   * compute forward/backward time-axis boundary limits without requiring the
   * current wall-clock time.
   */
  guideSessionStartMs: number;

  /**
   * Millisecond timestamp of the start time of the currently focused program cell.
   * Used as a time anchor during vertical (up/down) navigation so that the
   * highlight remains on the program airing at this time in each row.
   * 0 means unset (will be lazily initialised on first vertical press).
   * Updated on horizontal moves and time-axis shifts; preserved across vertical moves.
   */
  highlightAnchorTimeMs: number;

  /**
   * Whether the filter sidebar icon (favourites) is currently highlighted.
   * Set to true when the user presses LEFT from the channel cell (hPos 0) in
   * the current guide window. A second LEFT while this is true shifts the
   * time axis backward into the past guide.
   * Cleared on UP, DOWN, or RIGHT navigation.
   */
  isSidebarFocused: boolean;

  /**
   * IDs of the currently selected guide filters (multi-select): Favorites
   * and/or one or more categories at once. Channel-filtering logic matches
   * Favorites locally via `channel.isFavorite` and categories via
   * `categoryFilterChannelIds`/`categoryFilterResolutionStatus` — see
   * `matchesActiveGuideFilters` in guideNavigation.ts.
   */
  selectedFilterIds: string[];

  /**
   * Whether the user has at least one favorite channel anywhere in the full
   * (unfiltered) channel list. Not derivable from `channels`, which may
   * already be narrowed by another active filter. When false the Favorites
   * filter is disabled in the sidebar and removed from `selectedFilterIds`.
   */
  hasFavoriteChannels: boolean;

  /**
   * Channel ids returned by the last successful `/v2/guideChannelIds`
   * resolution for the current non-favorite portion of `selectedFilterIds`.
   * Meaningless (stale) whenever `categoryFilterResolutionStatus` is not
   * `"resolved"` — see that field's doc for the tri-state semantics.
   */
  categoryFilterChannelIds: string[];

  /**
   * Resolution status of the non-favorite (category) portion of
   * `selectedFilterIds` against `/v2/guideChannelIds`:
   * - `"idle"`: no non-favorite filter id is currently selected — nothing to
   *   resolve, and NOT a failure state.
   * - `"resolved"`: the last resolution attempt succeeded;
   *   `categoryFilterChannelIds` may legitimately be empty (zero matches).
   * - `"failed"`: the last resolution attempt errored;
   *   `categoryFilterChannelIds` holds the last-known-good list and must not
   *   be trusted as current.
   */
  categoryFilterResolutionStatus: "idle" | "resolved" | "failed";

  /**
   * Mirrors whether the filter sidebar's hover-dwell expanded (overlay)
   * presentation is currently open. Lifted from GuideFilterSidebar's local
   * `isExpanded` state so filter-toggle logic can decide whether selecting a
   * filter should auto-close the sidebar (collapsed quick-select) or leave it
   * open (expanded multi-select) — see `applyGuideFilterToggle`.
   */
  isFilterSidebarExpanded: boolean;

  /**
   * Grid position captured the moment the sidebar gained focus
   * (`setIsSidebarFocused(true)`), before `sidebarFocusIndex` is computed.
   * `closeGuideFilterSidebar()` restores this when closing an *expanded*
   * overlay, so the user returns to where they were before entering the
   * filter column rather than wherever the last toggle's anchor landed.
   * `null` when the sidebar is not currently focused.
   */
  preSidebarEntryPosition: {
    verticalPosition: number;
    horizontalPosition: number;
    channelScrollOffset: number;
  } | null;

  /**
   * Dynamically loaded filter items fetched from the API.
   * Falls back to a single Favorites item when the API fails or returns empty.
   */
  filterItems: TGuideFilterItem[];

  /**
   * Index of the currently highlighted filter sidebar category.
   * Valid range is 0..filterItems.length - 1.
   * Set to the nearest category each time the sidebar gains focus; moved by
   * UP/DOWN while the sidebar is focused.
   */
  sidebarFocusIndex: number;

  /**
   * Millisecond timestamp of when the sidebar last gained focus.
   * Used to enforce the directional lock (GUIDE_SIDEBAR_DIRECTIONAL_LOCK_MS)
   * after entering the filter panel.
   */
  sidebarEnteredAtMs: number;

  /**
   * Monotonically increasing "latest wins" counter. Every navigation change
   * that moves the visible window (vertical page load, horizontal time shift,
   * or settle) bumps it. Async loads snapshot it at dispatch and re-check it
   * before merging rows; a snapshot that no longer matches is discarded so
   * older async work can never overwrite newer navigation state.
   */
  windowLoadGeneration: number;

  /** Current navigation-engine mode (see GuideNavMode). */
  navMode: GuideNavMode;

  /**
   * True once the visible window's loading cells have exceeded
   * GUIDE_LOADING_CELL_TIMEOUT_MS. Drives the LOADING → TBA fallback; reset
   * when a new load cycle begins.
   */
  guideLoadingTimedOut: boolean;

  /**
   * Whether NPVR recording is allowed.
   */
  isNpvrRecordingAllowed: boolean;

  /**
   * Preview data for the currently focused guide program cell.
   * Null when no program is focused or the preview fetch has not yet completed.
   */
  previewData: TPreview | null;

  /**
   * offerId the current `previewData` was fetched for. `TPreview` carries no
   * offerId of its own, so this is how callers confirm previewData actually
   * describes the cell they're about to attach it to, rather than a just-
   * superseded selection's still-fulfilling result.
   */
  previewDataOfferId: string | null;

  /**
   * Whether a guide offer preview fetch is currently in progress.
   */
  isPreviewLoading: boolean;

  /**
   * Whether the guide-rows (EPG program schedule) service has failed to load
   * data for the current window.
   *
   * Channels are fetched independently and are unaffected by this flag. When
   * true, the grid renders a single focusable "Select to tune to channel"
   * fallback cell per row (in place of program cells), tuning bypasses the
   * currently-airing check, and past/future time-axis navigation is disabled
   * since no program schedule is available for other windows.
   */
  isGuideRowsFailed: boolean;

  /**
   * Sub-state for the jump-to-channel digit-entry overlay.
   * Manages the digit buffer, overlay visibility, and error flag.
   */
  jumpToChannel: GuideJumpToChannelState;
}
